import { ITPConfig, TPSettings } from '@stores/source';
import React, { useState } from 'react';
import { Body, H4, NewLabel, Subtext } from '@ui-library/typography';
import Button from '@ui-library/button';
import { Flex } from '@ui-library/misc';
import useStores from '@stores/useStores';
import useModalView from '@hooks/useModalView';
import Modal from '@ui-library/modal/custom';
import ConfigRow from '../configRow';
import { TRACKING_PLAN_DOCS } from '@components/trackingPlans/constants';
import { Container, RecommendedTag } from './styles';

interface Props {
  trackingPlanConfig: ITPConfig;
  isAdmin: boolean;
}

const otherViolationsDescription = (
  <Subtext>
    Drop all events with violations that include: Type Mismatch, Required Fields Missing, and others
    <Button textPrimary>
      <a href={TRACKING_PLAN_DOCS} target="_blank" rel="noopener noreferrer">
        outlined in our API.
      </a>
    </Button>
  </Subtext>
);

const unplannedEventsName = (
  <Flex>
    <NewLabel>Propagate errors</NewLabel>
    <RecommendedTag>Recommended</RecommendedTag>
  </Flex>
);

export default ({ trackingPlanConfig, isAdmin }: Props) => {
  const { sourceDataStore } = useStores();
  const [config, setConfig] = useState<TPSettings>(trackingPlanConfig.track);
  const [edit, setEdit] = useState(false);
  const [loading, setLoading] = useState(false);

  const toggleEdit = () => {
    setEdit(!edit);
  };

  const onCancel = () => {
    setEdit((curr) => !curr);
    setConfig(trackingPlanConfig.track);
  };

  const handleSave = async () => {
    setLoading(true);
    await sourceDataStore.updateTPSettings(config, () => {
      setEdit(false);
      showConfirmModal(false);
    });
    setLoading(false);
  };

  const handleOnChange = (key: keyof TPSettings, value: boolean) => {
    const updatedConfig = {
      ...config,
      [key]: value,
    };
    setConfig(updatedConfig);
  };

  const [confirmModal, showConfirmModal] = useModalView(
    <Modal
      title="Confirm Changes to Tracking Plan Settings"
      content={
        <Body>
          Making changes to your Tracking Plan Settings will alter the event payload information.
          Please confirm that you want to save this change.
        </Body>
      }
      onClose={() => showConfirmModal(false)}
      saveLabel="Confirm changes"
      onSave={handleSave}
      loading={loading}
      size="md"
    />,
  );
  return (
    <>
      {confirmModal}
      <Flex alignItemsCenter spaceBetween className="m-v-md">
        <H4>Tracking Plan Settings</H4>
        <Flex>
          {!edit && isAdmin && (
            <Button data-testid="edit-button" secondary name="Edit Settings" onClick={toggleEdit} />
          )}
          {edit && (
            <>
              <Button secondary className="m-r-md" name="Cancel" onClick={onCancel} />
              <Button
                data-testid="save-button"
                name="Save Settings"
                onClick={() => showConfirmModal(true)}
              />
            </>
          )}
        </Flex>
      </Flex>
      <Container>
        <ConfigRow
          name="Drop events with unplanned event names"
          description="Drop all events that do not match event names to the list of predefined event names."
          value={config.dropUnplannedEvents}
          edit={edit}
          onChange={(value) => handleOnChange('dropUnplannedEvents', value)}
        />
        <ConfigRow
          name="Drop events with unplanned event properties"
          description="Drop all events that have properties that do not match the list of predefined properties within the specific event."
          value={config.dropUnplannedProperties}
          edit={edit}
          onChange={(value) => handleOnChange('dropUnplannedProperties', value)}
        />
        <ConfigRow
          name="Drop events with other violations"
          description={otherViolationsDescription}
          value={config.dropOtherViolation}
          edit={edit}
          onChange={(value) => handleOnChange('dropOtherViolation', value)}
        />
        <ConfigRow
          name={unplannedEventsName}
          description="Propagate tracking plan error messages into event payload.
        Error messages will be available in Transformations as well as downstream destinations. Recommended to assess the performance of your tracking plans."
          value={config.propagateValidationErrors}
          edit={edit}
          onChange={(value) => handleOnChange('propagateValidationErrors', value)}
        />
      </Container>
    </>
  );
};
